import React from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App'
import reportWebVitals from './reportWebVitals'

import { Provider } from 'react-redux'
import { ClerkProvider } from '@clerk/clerk-react'

import { store } from './store'
import { ErrorBoundary } from './components'

const ReduxProvider = Provider as React.ComponentType<{
    store: typeof store
    children?: React.ReactNode
}>
const publishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || ''

// Suppress MUI/JSS "refs" unmount errors (can fire on schedule after login). Only redirect when on auth-callback.
function isClerkRefsError(message: string | undefined): boolean {
    if (typeof message !== 'string') return false
    return (
        message.includes('refs') ||
        message.includes("'refs'") ||
        message.includes("reading 'refs'") ||
        message.includes('reading "refs"') ||
        /Cannot read propert(y|ies) of undefined.*refs?/i.test(message)
    )
}

function handleClerkRefsError(): void {
    // Never redirect away from auth-callback: let the callback finish exchanging the Clerk token and redirect to schedule
    const hideOverlay = () => {
        document.querySelectorAll('body > div').forEach((el) => {
            if (el.id === 'root') return
            const style = window.getComputedStyle(el)
            if (style.position === 'fixed' && el.textContent?.includes('refs'))
                el.remove()
        })
    }
    setTimeout(hideOverlay, 0)
    setTimeout(hideOverlay, 200)
}

const prevOnError = window.onerror
window.onerror = function (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error,
) {
    if (isClerkRefsError(message as string)) {
        handleClerkRefsError()
        return true
    }
    if (prevOnError) return prevOnError(message, source, lineno, colno, error)
    return false
}

window.addEventListener(
    'error',
    (event) => {
        if (isClerkRefsError(event?.message)) {
            event.preventDefault()
            event.stopPropagation()
            event.stopImmediatePropagation()
            handleClerkRefsError()
        }
    },
    true,
)

const container = document.getElementById('root')
if (container) {
    const root = createRoot(container)
    root.render(
        <ClerkProvider publishableKey={publishableKey}>
            <ErrorBoundary>
                <ReduxProvider store={store}>
                    <App />
                </ReduxProvider>
            </ErrorBoundary>
        </ClerkProvider>,
    )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
